import { forwardRef } from 'react';
import { Trophy } from 'lucide-react';
import logoIcon from '../../assets/logo-icon.png';

export interface RoundMatchLine {
  team1: string;
  team2: string;
  score: string;
  isWo: boolean;
}

interface RankingRoundStoryCardProps {
  rankingName: string;
  roundNumber: number;
  rankingImageUrl: string | null;
  photoDataUrl: string | null;
  matches: RoundMatchLine[];
}

export const RankingRoundStoryCard = forwardRef<HTMLDivElement, RankingRoundStoryCardProps>(
  ({ rankingName, roundNumber, rankingImageUrl, photoDataUrl, matches }, ref) => {
    return (
      <div
        ref={ref}
        className="w-[360px] h-[640px] flex flex-col overflow-hidden"
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          background: 'linear-gradient(160deg, #0A1128 0%, #1a2744 40%, #0f1d35 100%)',
          position: 'relative',
        }}
      >
        {/* Decorative gradient blobs */}
        <div style={{
          position: 'absolute',
          top: -70,
          right: -70,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6,182,212,0.18) 0%, transparent 70%)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -60,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,107,0.12) 0%, transparent 70%)',
        }} />

        {/* Top bar — BT Vision branding (padded for Instagram UI safe zone) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          height: 88,
          flexShrink: 0,
          paddingTop: 36,
          paddingBottom: 0,
          position: 'relative',
          zIndex: 1,
        }}>
          <img src={logoIcon} alt="" style={{ height: 22, width: 'auto' }} />
          <span
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 17,
              background: 'linear-gradient(135deg, #40E0D0 0%, #FFD700 50%, #FF6B6B 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 0.2,
            }}
          >
            BT Vision
          </span>
        </div>

        {/* Middle — user photo in 3:4 (240×320) */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{
            width: 360,
            height: 360,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: 'inset 4px 4px 6px rgba(0, 0, 0, 0.3), inset -4px -4px 6px rgba(255, 255, 255, 0.5)',
            background: 'rgba(255,255,255,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {photoDataUrl ? (
              <img
                src={photoDataUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>foto</span>
            )}
          </div>
        </div>

        {/* Bottom bar — ranking info + scores (padded for Instagram reply UI) */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 16,
          paddingLeft: 20,
          paddingRight: 20,
          paddingBottom: 92,
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Header: thumbnail + "Ranking <name>" + rodada — centered */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 12,
          }}>
            <div style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              overflow: 'hidden',
              flexShrink: 0,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {rankingImageUrl ? (
                <img
                  src={rankingImageUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Trophy style={{ width: 18, height: 18, color: '#40E0D0' }} />
              )}
            </div>
            <div style={{ minWidth: 0, maxWidth: 240, textAlign: 'left' }}>
              <p style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                fontSize: 15,
                color: '#fff',
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                Ranking {rankingName}
              </p>
              <p style={{
                marginTop: 1,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: '#40E0D0',
              }}>
                Rodada {roundNumber}
              </p>
            </div>
          </div>

          {/* Match lines */}
          <div style={{
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}>
            {matches.map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '2px 6px',
                }}
              >
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 10.5,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.85)',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {m.team1}
                </div>
                <div style={{
                  flexShrink: 0,
                  minWidth: 70,
                  padding: '0px 8px',
                  margin: '0px 4px',
                  borderRadius: 7,
                  background: m.isWo ? 'rgba(251,216,93,0.12)' : 'rgba(6,182,212,0.14)',
                  border: m.isWo
                    ? '1px solid rgba(251,216,93,0.3)'
                    : '1px solid rgba(6,182,212,0.3)',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 500,
                  fontSize: 11,
                  color: m.isWo ? '#FBD85D' : '#40E0D0',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: 1,
                }}>
                  {m.isWo ? 'W.O.' : m.score}
                </div>
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 10.5,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.85)',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {m.team2}
                </div>
              </div>
            ))}
          </div>

          {/* Footer URL */}
          <p style={{
            marginTop: 10,
            textAlign: 'center',
            fontSize: 9,
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: 0.3,
          }}>
            btvision.app
          </p>
        </div>
      </div>
    );
  }
);

RankingRoundStoryCard.displayName = 'RankingRoundStoryCard';
